import React from 'react'
import { Trans } from '@lingui/macro'
import {
  Maybe,
  PathwayModelAsStudent,
  PathwayProgress,
  TopicModelAsMember
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import {
  ResourceCard,
  resourceCardCoverImageSrcSet
} from '../workspace-page/resource-card/ResourceCard'
import CardFlow from '../../components/common/card-flow/CardFlow'
import { PathwayCompletedStatusEnum } from '../../../../types/Pathway'
import {
  PathwayEnrolmentStatusEnum,
  WorkbookStatusEnum
} from '../../graphql/private/PrivateSchemaTypes'
import { useParams } from 'react-router-dom'
import { isTopicCompletedOutsidePathway } from './helpers'
import { PathwayDetailPageContext } from './PathwayDetailPageContext'

export const PathwayTopicCards = () => {
  const { pathwayVersion } =
    useParams<{ pathwayId: string; pathwayVersion?: string }>()

  const { pathway } = React.useContext(PathwayDetailPageContext)

  if (!pathway) {
    return null
  }

  const version = pathwayVersion
    ? pathway.specificVersion && pathway.specificVersion[0]
    : pathway.latestVersion && pathway.latestVersion[0]

  let focusedTopic: Partial<TopicModelAsMember> | undefined

  /**
   * index of a topic that should be done next by the user
   */
  const focusedIndex = Math.max(
    // find first topic index for which we have unfinished enrolment
    version?.topics?.findIndex((topic) => {
      const te = pathway.currentEnrolment?.topicEnrolments.find(
        (te) => te.topic_id === topic.id
      )
      const isCompletedOutside = isTopicCompletedOutsidePathway(
        te?.status,
        topic.lastEnrolment?.status,
        topic.lastEnrolment?.completion_date,
        pathway.currentEnrolment?.completed_at
      )

      if (pathway.progress === PathwayProgress.locked) {
        return (
          (!te?.completion_date && !isCompletedOutside) ||
          te?.status === WorkbookStatusEnum.FAILED
        ) // in this case user will have to retry the same resource again even though it is completed
      }
      // no completion date present? user will have to do this one next
      return (
        (!te?.completion_date && !isCompletedOutside) ||
        (te?.completion_date &&
          te?.status === WorkbookStatusEnum.FAILED &&
          te?.topicVersion?.allow_retry)
      )
    }) ?? 0,
    0
  )

  if (version?.topics) {
    focusedTopic = version?.topics[focusedIndex] as Partial<TopicModelAsMember>
  }

  let firstTimeDisabled = false

  return (
    <CardFlow
      cards={
        version?.topics?.map((topic, topicIndex) => {
          const currentTopicEnrolment =
            pathway.currentEnrolment?.topicEnrolments.find(
              (te) => te.topic_id === topic.id
            )

          const {
            currentTopicVersion,
            author,
            coverImage,
            coverImageVariants,
            id: topic_id
          } = topic

          let image: Maybe<string> | undefined = null

          const isFailed =
            pathway.currentEnrolment?.completed_status ===
            PathwayCompletedStatusEnum.FAILED

          const isCompleted =
            pathway.currentEnrolment?.status ===
            PathwayEnrolmentStatusEnum.COMPLETED

          if (coverImage) {
            const resizedImage = coverImageVariants.find(
              (imageVariant) => imageVariant.size_key === '300x200'
            )
            image = resizedImage ? resizedImage.cdnUrl : coverImage.cdnUrl
          }

          const isBehindFocused =
            focusedIndex !== null && topicIndex > focusedIndex

          const isDisabled =
            isBehindFocused &&
            (pathway.progress === PathwayProgress.linear ||
              pathway.progress === PathwayProgress.locked)

          const isFirstTimeDisabled = isDisabled && !firstTimeDisabled
          if (isDisabled) {
            firstTimeDisabled = true
          }

          const topicVersion = getTopicVersion(
            currentTopicEnrolment,
            currentTopicVersion
          )
          const isNext =
            topic === focusedTopic &&
            !currentTopicEnrolment &&
            !isCompleted &&
            !isFailed

          const showRetry =
            topic === focusedTopic &&
            currentTopicEnrolment?.status === WorkbookStatusEnum.FAILED &&
            currentTopicEnrolment?.topicVersion?.allow_retry

          const showCta = showRetry || isNext

          const isFirst = topicIndex === 0

          return (
            <ResourceCard
              key={`ResourceCard-${topicIndex}`}
              disabled={
                isDisabled &&
                !currentTopicEnrolment?.completion_date &&
                !pathwayVersion
              }
              failedPathway={isFailed && !pathwayVersion}
              firstTimeDisabled={isFirstTimeDisabled && !pathwayVersion}
              title={currentTopicVersion?.title}
              description={currentTopicVersion?.summary}
              enrolment={!pathwayVersion ? currentTopicEnrolment : undefined}
              author={{ display_name: author.display_name }}
              readDurationMins={topicVersion.readDurationMins}
              image={image}
              topicId={topic_id}
              pathway={pathway as unknown as Partial<PathwayModelAsStudent>}
              imageSrcSet={resourceCardCoverImageSrcSet(coverImageVariants)}
              ctaButton={
                showCta &&
                !pathwayVersion &&
                (showRetry ? (
                  <Trans>Try again</Trans>
                ) : isFirst ? (
                  <Trans>Start here</Trans>
                ) : (
                  <Trans>Up next</Trans>
                ))
              }
            />
          )
        }) ?? []
      }
      cardsConnected={pathway.progress !== PathwayProgress.open}
      focusedTopicIndex={focusedIndex}
    />
  )
}

const getTopicVersion = (topicEnrolment, currentTopicVersion) => {
  // If enrolment has assigned topic version, it has higher priority than topic's current version
  // topic enrolment might be missing when user did not open topic in that pathway yet.
  if (!topicEnrolment || !topicEnrolment?.topicVersion?.title) {
    return currentTopicVersion
  }
  return topicEnrolment.topicVersion
}
